import React, { useMemo } from "react";
import clsx from "clsx";
import { getIn } from "formik";
import {
  FormGroup,
  Label,
  Col,
  Input,
  Tooltip,
  Icon,
  Select,
  DatePicker,
} from "orion";
import InformationSVG from "assets/icons/information.svg";
import PhoneInput from "shared/PhoneInput/PhoneInput";
import InfoGreyOutline from "@druva-apps/orion-icons/lib/InfoGreyOutline";

import GenericOptionsSelect from "components/GenericOptionsSelect";
import styles from "./Field.module.scss";
import {
  genericSelectTypes,
  minDate as defaultMinDate,
  minYear,
  maxYear,
} from "consts";
import Checkbox from "./Checkbox";
import RadioInput from "./radio-input";
import StorageRegionsSelect from "./async-select/storage-regions-select";

export const getIsInputVaild = (formikProps, name) =>
  getIn(formikProps.touched, name) &&
  getIn(formikProps.errors, name) !== undefined;

const Field = ({
  name,
  label,
  placeholder,
  options,
  formikProps,
  type = "text",
  tooltip,
  tooltipProps,
  isMulti,
  dataTestId,
  full,
  fixedInitialValues,
  disabled,
  interceptor,
  minDate,
  maxDate,
  onChange,
  productId,
  downgradeAllowed,
  isEndpointAllowedForEdition,
  disabledEditions,
  onBlur,
  checkboxExtraLabel,
  checkboxTooltipProps,
  labelTooltip,
  labelSpan,
  optionsQueryConfig,
  isClearable,
  iterator,
  inputTooltipProps,
  isLoading,
}) => {
  const isInvalid = useMemo(
    () => getIsInputVaild(formikProps, name),
    [formikProps.errors, formikProps.touched, name]
  );

  const getInputField = () => {
    switch (type) {
      case "number": {
        return (
          <Input
            type="number"
            className={styles.numberField}
            name={name}
            id={name}
            validationProps={formikProps}
            isInvalid={isInvalid}
            dataTestId={dataTestId}
            placeholder={placeholder}
            disabled={disabled}
            onBlur={onBlur}
          />
        );
      }
      case "textarea": {
        return (
          <Input
            type="textarea"
            name={name}
            id={name}
            validationProps={formikProps}
            isInvalid={isInvalid}
            dataTestId={dataTestId}
            placeholder={placeholder}
            disabled={disabled}
            onBlur={onBlur}
          />
        );
      }
      case "radio": {
        return (
          <RadioInput
            type="radio"
            name={name}
            id={name}
            options={options}
            validationProps={formikProps}
            disabled={disabled}
            isInvalid={isInvalid}
            dataTestId={dataTestId}
            onBlur={onBlur}
          />
        );
      }
      case "text": {
        return (
          <Input
            type="text"
            name={name}
            id={name}
            validationProps={formikProps}
            isInvalid={isInvalid}
            dataTestId={dataTestId}
            placeholder={placeholder}
            disabled={disabled}
            onBlur={onBlur}
          />
        );
      }
      case "phone": {
        return (
          <PhoneInput
            name={name}
            value={getIn(formikProps.values, name)}
            formikProps={formikProps}
            isInvalid={isInvalid}
            onChange={(phone) => {
              formikProps.setFieldValue(name, phone);
            }}
            dataTestId={dataTestId}
            placeholder={placeholder}
            disabled={disabled}
          />
        );
      }
      case "storage-regions-select": {
        return (
          <StorageRegionsSelect
            isMulti={isMulti}
            name={name}
            type={type}
            placeholder={placeholder}
            formikProps={formikProps}
            dataTestId={dataTestId}
            isInvalid={isInvalid}
            disabled={disabled}
            onChange={onChange}
            optionsQueryConfig={optionsQueryConfig}
            iterator={iterator}
            isClearable={isClearable}
          />
        );
      }
      case genericSelectTypes.skus:
      case genericSelectTypes.editions:
      case genericSelectTypes.datasources: {
        return (
          <GenericOptionsSelect
            isMulti={isMulti}
            name={name}
            type={type}
            placeholder={placeholder}
            formikProps={formikProps}
            dataTestId={dataTestId}
            isInvalid={isInvalid}
            disabled={disabled}
            fixedInitialValues={fixedInitialValues}
            onChange={onChange}
            productId={productId}
            downgradeAllowed={downgradeAllowed}
            isEndpointAllowedForEdition={isEndpointAllowedForEdition}
            disabledEditions={disabledEditions}
          />
        );
      }
      case "select": {
        return (
          <Select
            isMulti={isMulti}
            name={name}
            type="select"
            options={options}
            placeholder={placeholder}
            isInvalid={isInvalid}
            validationProps={formikProps}
            value={getIn(formikProps.values, name)}
            dataTestId={dataTestId}
            disabled={disabled}
            isClearable={isClearable}
            onChange={onChange}
            isDisabled={disabled}
            isLoading={isLoading}
          />
        );
      }
      case "checkbox": {
        return (
          <Checkbox
            name={name}
            id={name}
            validationProps={formikProps}
            isInvalid={isInvalid}
            dataTestId={dataTestId}
            disabled={disabled}
            checkboxExtraLabel={checkboxExtraLabel}
            tooltipProps={checkboxTooltipProps}
          />
        );
      }
      default: {
        return (
          <DatePicker
            isClearable={false}
            dateFormat="MMM dd, yyyy"
            name={name}
            type="select"
            placeholder={placeholder}
            isInvalid={isInvalid}
            validationProps={formikProps}
            value={getIn(formikProps.values, name)}
            dataTestId={dataTestId}
            minDate={minDate}
            maxDate={maxDate}
            startYear={minYear}
            endYear={maxYear}
            disabled={disabled}
            onChange={(nativeDate) => {
              if (interceptor) {
                formikProps.setFieldValue(name, interceptor(nativeDate));
              }
            }}
          />
        );
      }
    }
  };

  return (
    <FormGroup
      row
      className={clsx({
        "oui-mb-3": type === "checkbox",
        "oui-mb-0": type !== "checkbox",
      })}
    >
      <Col sm={labelSpan || 3} className="oui-d-flex">
        <Label muted text={label} />
        {labelTooltip && (
          <div>
            <Tooltip
              maxWidth={250}
              placement="bottom"
              text={labelTooltip}
              {...tooltipProps}
            >
              <InfoGreyOutline muted={false} className="oui-ml-2" />
            </Tooltip>
          </div>
        )}
      </Col>
      <Col
        sm={type === "number" ? 2 : full ? 9 : 5}
        className={clsx(
          "oui-mt-auto",
          isInvalid || type === "checkbox" ? "oui-mb-1" : "oui-mb-3"
        )}
      >
        {inputTooltipProps ? (
          <Tooltip {...inputTooltipProps}>
            <div className={inputTooltipProps?.parentClassName}>
              {getInputField()}
            </div>
          </Tooltip>
        ) : (
          getInputField()
        )}
      </Col>
      {tooltip && (
        <Col
          sm={3}
          className={`oui-pl-0 oui-my-auto ${styles.tooltipContainer}`}
        >
          <Tooltip
            maxWidth={275}
            placement="right"
            text={tooltip}
            {...tooltipProps}
          >
            <Icon src={InformationSVG} />
          </Tooltip>
        </Col>
      )}
    </FormGroup>
  );
};

Field.defaultProps = {
  minDate: defaultMinDate,
  type: "text",
};

export default Field;
